'use client';

import { ApiType, getApiInstance } from '@/lib/config/axios';
import { queryClient } from '@/lib/config/react-query';
import { AxiosApiError } from '@/lib/config/types';
import {
  MutationKey,
  QueryFunction,
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';

type UseQueryFetchOptions<TData, TError, TParams = Record<string, unknown>, TVariables = any> = {
  key: QueryKey | MutationKey;
  url: string;
  params?: TParams;
  apiType?: ApiType;
  options?: Partial<UseQueryOptions<TData, TError> | UseMutationOptions<TData, TError, TVariables>>;
};

type UseMutationFetchOptions<TData, TError, TVariables> = {
  key: MutationKey;
  url: string;
  apiType?: ApiType;
  options?: UseMutationOptions<TData, TError, TVariables>;
  getRequestConfig?: () => AxiosRequestConfig;
};

export function useGet<TData = unknown, TError = AxiosApiError>({
  key,
  url,
  params,
  apiType = 'api',
  options,
  onSuccess,
  onError,
  onFinally,
}: UseQueryFetchOptions<TData, TError> & {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  onFinally?: () => void;
}): UseQueryResult<TData, TError> {
  const apiInstance = getApiInstance(apiType);

  const enhancedQueryFn = (async (_) => {
    try {
      const response = await apiInstance.get<TData>(url, params);
      if (onSuccess) await onSuccess(response.data);
      return response.data;
    } catch (error) {
      if (onError) {
        onError(error as TError);
      }

      throw error;
    } finally {
      if (onFinally) onFinally();
    }
  }) as QueryFunction<TData, QueryKey>;

  return useQuery<TData, TError>({
    queryKey: key,
    queryFn: enhancedQueryFn,
    ...options,
  });
}

export function useList<
  TData = unknown,
  TError = AxiosApiError,
  TParams = Record<string, unknown>,
>({
  key,
  url,
  params,
  apiType = 'api',
  options,
  onSuccess,
  onError,
  onFinally,
}: UseQueryFetchOptions<TData, TError, TParams> & {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  onFinally?: () => void;
}): UseQueryResult<TData, TError> {
  const apiInstance = getApiInstance(apiType);

  const enhancedQueryFn = (async (_) => {
    try {
      const response = await apiInstance.post<TData>(url, params);
      if (onSuccess) await onSuccess(response.data);
      return response.data;
    } catch (error) {
      if (onError) {
        onError(error as TError);
      }

      throw error;
    } finally {
      if (onFinally) onFinally();
    }
  }) as QueryFunction<TData, QueryKey>;

  return useQuery<TData, TError>({
    queryKey: key,
    queryFn: enhancedQueryFn,
    ...options,
  });
}

export function usePost<TData = unknown, TError = AxiosApiError, TVariables = unknown>({
  key,
  url,
  params,
  apiType = 'api',
  options,
  getRequestConfig,
}: UseMutationFetchOptions<TData, TError, TVariables> & {
  params?: TVariables;
}) {
  const apiInstance = getApiInstance(apiType);

  return useMutation<TData, TError, TVariables>({
    mutationKey: key,
    mutationFn: async (variables: TVariables = params as TVariables) => {
      const response = await apiInstance.post<TData>(url, variables, getRequestConfig?.());
      return response.data;
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        predicate: (queryKey) => key.includes(queryKey),
      }),
    ...options,
  });
}

export function useDelete<TData = unknown, TError = AxiosApiError, TVariables = unknown>({
  key,
  url,
  apiType = 'api',
  options,
}: UseMutationFetchOptions<TData, TError, TVariables>): UseMutationResult<
  TData,
  TError,
  TVariables
> {
  const apiInstance = getApiInstance(apiType);

  return useMutation<TData, TError, TVariables>({
    mutationKey: key,
    mutationFn: async (variables: TVariables) => {
      const response = await apiInstance.delete<TData>(url, { data: variables });
      return response.data;
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        predicate: (queryKey) => {
          return key.includes(queryKey);
        },
      }),
    ...options,
  });
}

export function usePatch<TData = unknown, TError = AxiosApiError, TVariables = unknown>({
  key,
  url,
  apiType = 'api',
  options,
}: UseMutationFetchOptions<TData, TError, TVariables>): UseMutationResult<
  TData,
  TError,
  TVariables
> {
  const apiInstance = getApiInstance(apiType);

  return useMutation<TData, TError, TVariables>({
    mutationKey: key,
    mutationFn: async (variables: TVariables) => {
      const response = await apiInstance.patch<TData>(url, variables);
      return response.data;
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        predicate: (queryKey) => {
          return key.includes(queryKey);
        },
      }),
    ...options,
  });
}

export function useGetMutation<TData = unknown, TError = AxiosApiError, TVariables = unknown>({
  key,
  url,
  apiType = 'api',
  options,
  urlBuilder,
  responseType,
}: UseMutationFetchOptions<TData, TError, TVariables> & {
  urlBuilder?: (variables: TVariables) => string;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
}): UseMutationResult<TData, TError, TVariables> {
  const apiInstance = getApiInstance(apiType);

  return useMutation<TData, TError, TVariables>({
    mutationKey: key,
    mutationFn: async (variables: TVariables) => {
      let finalUrl = url;
      if (urlBuilder) finalUrl = urlBuilder(variables);
      const response = await apiInstance.get<TData>(finalUrl, {
        params: variables,
        responseType,
      });
      return response.data;
    },
    ...options,
  });
}
